/**
 * 会員API統合クライアント（完全版・改修済み）
 * 
 * /api/meエンドポイントの呼び出し前に自動でCSRFトークンを取得し、
 * X-CSRF-TOKENヘッダーに追加してリクエストを送信します。
 */
import { getEnvironmentConfig } from '../config/apiEndpoints';
import { csrfTokenService } from './csrfTokenService';

class MemberApiClient {
  constructor(config = {}) {
    this._initializeConfig(config);
  }

  _initializeConfig(config) {
    const defaultConfig = getEnvironmentConfig();
    
    this.baseUrl = config.baseUrl || defaultConfig.baseUrl;
    this.timeout = config.timeout || defaultConfig.timeout;
    this.environment = config.environment || defaultConfig.environment;
    this.endpoints = {
      ...defaultConfig.endpoints,
      ...config.endpoints
    };
  }

  /**
   * HTTP リクエスト実行（基本版）
   * @private
   */
  async _request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;

    const defaultOptions = {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Environment': this.environment,
        ...options.headers
      }
    };

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const response = await fetch(url, { 
        ...defaultOptions, 
        ...options,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      const result = {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      };

      if (response.headers.get('content-type')?.includes('application/json')) {
        result.data = await response.json();
      } else {
        result.text = await response.text();
      }

      return result;

    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error.name === 'AbortError') {
        throw new Error('リクエストがタイムアウトしました');
      }
      
      throw error;
    }
  }

  /**
   * CSRF保護が必要な /api/me エンドポイント用リクエスト実行（改修版）
   * @private
   */
  async _requestWithCsrf(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const method = options.method || 'GET';

    // executeWithCsrfを使用してトークン取得の競合を回避
    return csrfTokenService.executeWithCsrf(async (csrfToken) => {
      const headers = {
        'Content-Type': 'application/json',
        'X-Environment': this.environment,
        ...options.headers
      };

      // POST/PUT/PATCH/DELETE メソッドの場合のみCSRFトークンを追加
      if (method !== 'GET') {
        headers['X-CSRF-TOKEN'] = csrfToken;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      try {
        const response = await fetch(url, {
          method,
          headers,
          credentials: 'include',
          signal: controller.signal,
          ...options
        });
        
        clearTimeout(timeoutId);

        const result = {
          status: response.status,
          statusText: response.statusText,
          ok: response.ok
        };

        if (response.headers.get('content-type')?.includes('application/json')) {
          result.data = await response.json();
        } else {
          result.text = await response.text();
        }
        return result;

      } catch (error) {
        clearTimeout(timeoutId);
        
        if (error.name === 'AbortError') {
          throw new Error('リクエストがタイムアウトしました');
        }
        
        if (error.message.includes('セキュリティ') || error.message.includes('認証')) {
          throw error;
        }
        
        console.error(`[MEMBER API CLIENT] Request failed:`, error);
        throw new Error(`APIリクエストに失敗しました: ${error.message}`);
      }
    });
  }

  /**
   * 現在の会員情報を取得
   * @returns {Promise<Object|null>} 会員データまたはnull
   */
  async getCurrentMember() {
    try {
      
      const result = await this._request(this.endpoints.MEMBER.ME, {
        method: 'GET'
      });

      if (result.ok && result.data) {
        return result.data;
      }

      if (result.status === 404) {
        return null;
      }

      const errorMessage = result.data?.message || `会員データの取得に失敗しました: ${result.status}`;
      throw new Error(errorMessage);

    } catch (error) {
      console.error('[MEMBER API CLIENT] Failed to fetch current member:', error);
      throw error;
    }
  }

  /**
   * 現在の会員情報を更新（CSRF対応）
   * @param {Object} updateData - 更新するデータ
   * @returns {Promise<Object>} 更新結果
   */
  async updateCurrentMember(updateData) {
    try {

      const result = await this._requestWithCsrf(this.endpoints.MEMBER.ME, {
        method: 'PUT',
        body: JSON.stringify(updateData)
      });

      if (result.ok && result.data) {
        return {
          success: true,
          member: result.data.member || result.data,
          message: result.data.message || '会員情報を更新しました'
        };
      }

      const errorMessage = result.data?.message || `会員情報の更新に失敗しました: ${result.status}`;
      throw new Error(errorMessage);

    } catch (error) {
      console.error('[MEMBER API CLIENT] Failed to update member:', error);
      return {
        success: false,
        error: error.message || '会員情報の更新に失敗しました'
      };
    }
  }

  /**
   * パスワード変更（CSRF対応）
   * @param {Object} passwordData - パスワード変更データ
   * @returns {Promise<Object>} 変更結果
   */
  async changePassword(passwordData) {
    try {

      const result = await this._requestWithCsrf(this.endpoints.MEMBER.PASSWORD, {
        method: 'PUT',
        body: JSON.stringify(passwordData)
      });

      if (result.ok && result.data) {
        return {
          success: true,
          message: result.data.message || 'パスワードを変更しました'
        };
      }

      const errorMessage = result.data?.message || `パスワード変更に失敗しました: ${result.status}`;
      throw new Error(errorMessage);

    } catch (error) {
      console.error('[MEMBER API CLIENT] Failed to change password:', error);
      return {
        success: false,
        error: error.message || 'パスワード変更に失敗しました'
      };
    }
  }

  /**
   * メールアドレスで会員を検索
   * @param {string} email - メールアドレス
   * @param {string} token - 認証トークン
   * @returns {Promise<Object|null>} 会員データまたはnull
   */
  async getMemberByEmail(email, token) {
    try {
      
      const endpoint = this.endpoints.MEMBER.BY_EMAIL.replace('{email}', encodeURIComponent(email));
      
      const result = await this._request(endpoint, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (result.ok && result.data) {
        return result.data;
      }

      if (result.status === 404) {
        return null;
      }

      const errorMessage = result.data?.message || `会員検索に失敗しました: ${result.status}`;
      throw new Error(errorMessage);

    } catch (error) {
      console.error('[MEMBER API CLIENT] Failed to search member by email:', error);
      throw error;
    }
  }

  /**
   * Firebase UIDで会員を検索
   * @param {string} firebaseUid - Firebase UID
   * @param {string} token - 認証トークン
   * @returns {Promise<Object|null>} 会員データまたはnull
   */
  async getMemberByFirebaseUid(firebaseUid, token) {
    try {
      
      const endpoint = this.endpoints.MEMBER.BY_FIREBASE_UID.replace('{firebaseUid}', firebaseUid);
      
      const result = await this._request(endpoint, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (result.ok && result.data) {
        return result.data;
      }

      if (result.status === 404) {
        return null;
      }

      const errorMessage = result.data?.message || `会員検索に失敗しました: ${result.status}`;
      throw new Error(errorMessage);

    } catch (error) {
      console.error('[MEMBER API CLIENT] Failed to search member by Firebase UID:', error);
      throw error;
    }
  }

  /**
   * 会員登録（CSRF対応）
   * @param {Object} memberData - 会員登録データ
   * @returns {Promise<Object>} 登録結果
   */
  async registerMember(memberData) {
    try {

      const result = await this._requestWithCsrf(this.endpoints.MEMBER.REGISTER, {
        method: 'POST',
        body: JSON.stringify(memberData)
      });

      if (result.ok && result.data) {
        return {
          success: true,
          member: result.data.member || result.data,
          message: result.data.message || '会員登録が完了しました'
        };
      }

      const errorMessage = result.data?.message || `会員登録に失敗しました: ${result.status}`;
      throw new Error(errorMessage);

    } catch (error) {
      console.error('[MEMBER API CLIENT] Failed to register member:', error);
      return {
        success: false,
        error: error.message || '会員登録に失敗しました'
      };
    }
  }

  /**
   * マニュアルURL取得（CSRF対応）
   * @param {string} manualType - マニュアルタイプ
   * @returns {Promise<Object>} マニュアル情報
   */
  async getManualUrl(manualType) {
    try {
      
      const result = await this._requestWithCsrf(this.endpoints.MANUAL.GET_URL, {
        method: 'POST',
        body: JSON.stringify({ manualType })
      });

      if (result.ok && result.data) {
        return {
          success: true,
          url: result.data.url,
          message: result.data.message
        };
      }

      const errorMessage = result.data?.message || `マニュアルURLの取得に失敗しました: ${result.status}`;
      throw new Error(errorMessage);

    } catch (error) {
      console.error('[MEMBER API CLIENT] Failed to get manual URL:', error);
      throw error;
    }
  }

  /**
   * マニュアル一覧取得
   * @returns {Promise<Array>} マニュアル一覧
   */
  async getManualList() {
    try {
      
      const result = await this._request(this.endpoints.MANUAL.LIST, {
        method: 'GET'
      });

      if (result.ok && result.data) {
        return result.data.manuals || result.data;
      }

      const errorMessage = result.data?.message || `マニュアル一覧の取得に失敗しました: ${result.status}`;
      throw new Error(errorMessage);

    } catch (error) {
      console.error('[MEMBER API CLIENT] Failed to get manual list:', error);
      throw error;
    }
  }

  /**
   * デバッグ用の設定情報取得
   * @returns {Object} 設定情報
   */
  getDebugInfo() {
    return {
      baseUrl: this.baseUrl,
      timeout: this.timeout,
      environment: this.environment,
      endpoints: Object.keys(this.endpoints).length + ' endpoint groups configured'
    };
  }
}

// デフォルトインスタンス
export const memberApiClient = new MemberApiClient();
export default MemberApiClient;